import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation, gql } from '@apollo/client';
import { DisplayText, ToggleButton } from '@superops/ui-design-system';
import AIFeaturesHook from '@nile/components/src/Utils/aiFeaturesHook';
import { PromptInputSection } from '../../PromptInputSection';
import { MLClient } from '@nile/graphql/src';
import { getTranslatedText, toast } from '@nile/components';
import {
  renderFeaturesList,
  handleToggleFeature as handleToggleFeatureUtil
} from '@nile/components/src/Components/AI/components/AIOnboarding/utils/aiFeatureUtils';

const GENERATE_PROMPT_MUTATION = gql`
  mutation GeneratePrompt($userInput: String!) {
    generatePrompt(userInput: $userInput) {
      success
      ... on SuccessfulGeneratedPrompt {
        featureId
        name
        description
        prompt
      }
      ... on UnsuccessfulGeneratedPrompt {
        message
      }
    }
  }
`;

export const BubbleText: React.FC<{ text: React.ReactNode }> = ({ text }) => (
  <div className="flex justify-end mb10">
    <div className="ai-bubble p10 w-max-content">{text}</div>
  </div>
);

export const CreatePrompt: React.FC<{}> = (props) => {
  const intl = useIntl();
  const [showBubble, setShowBubble] = React.useState(false);
  const [promptFeatures, setPromptFeatures] = useState<any[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [clearInput, setClearInput] = useState(false);

  const prompts = props?.features || [];

  const promptLimit = 5;
  const [isPromptLimitExceeded, setIsPromptLimitExceeded] = useState(
    prompts.length >= promptLimit
  );

  useEffect(() => {
    setIsPromptLimitExceeded(
      prompts.length + promptFeatures.length >= promptLimit
    );
  }, [prompts.length, promptFeatures.length]);

  const {
    getAIFeatureData,
    updateAIFeature,
    aiFeatures,
    getAIFeaturesData
  } = AIFeaturesHook();

  const [generatePrompt] = useMutation(GENERATE_PROMPT_MUTATION, {
    client: MLClient
  });

  // useEffect(() => {
  //   const data = getAIFeatureData('PROMPT');
  //   console.log('AI Feature Data:', data);
  //   console.log('AI Features from hook:', aiFeatures);

  //   // Set initial prompt features state
  //   if (data?.features) {
  //     // setPromptFeatures(data.features);
  //   }
  // }, []);

  const handleCreatePrompt = async (promptText: string) => {
    if (!promptText.trim()) return;

    setIsCreating(true);
    setClearInput(false);

    try {
      const { data } = await generatePrompt({
        variables: { userInput: promptText }
      });

      const result = data.generatePrompt;

      if (result.success && result.featureId) {
        // Success case - update local state with new prompt
        const newPrompt = {
          featureId: result.featureId,
          name: result.name,
          description: result.description,
          prompt: result.prompt
        };

        setPromptFeatures((prev) => [...prev, newPrompt]);
        setShowBubble(true);
        setClearInput(true);
        getAIFeaturesData();
      } else {
        // Error case
        toast('error', result.message);
        // You might want to show an error message to the user here
      }
    } catch (error) {
      console.error('Error creating prompt:', error);
      toast(
        'error',
        getTranslatedText(intl, 'error.page.config.default.title')
      );
      // Handle network or other errors
    } finally {
      setIsCreating(false);
    }
  };

  const handleTogglePromptFeature = async (
    featureId: string,
    currentState: string
  ) => {
    if (updateAIFeature) {
      await handleToggleFeatureUtil({
        featureId,
        currentState,
        updateFunction: updateAIFeature,
        setFeatures: setPromptFeatures,
        showToastOnError: true
      });
    }
  };

  const renderPromptFeaturesList = () => {
    return renderFeaturesList({
      features: promptFeatures,
      onToggleFeature: handleTogglePromptFeature,
      useSubFeatures: false
    });
  };

  return (
    <div className="mt-15">
      {/* Render prompt features list if any exist */}
      {promptFeatures.length > 0 && (
        <div className="mb-20">
          <div className="mb-15">
            <DisplayText size="large" weight="Bold" variant="subdued">
              <FormattedMessage id="app.common.custom" />{' '}
              <FormattedMessage id="common.prompt" />
            </DisplayText>
          </div>
          <div className="bg-newblack p10 br-5">
            {renderPromptFeaturesList()}
          </div>
        </div>
      )}

      {showBubble && (
        <div className="mb10">
          <BubbleText
            text={getTranslatedText(intl, 'ai.onboarding.createprompt.success')}
          />
        </div>
      )}

      {!isPromptLimitExceeded && (
        <PromptInputSection
          onCreatePrompt={handleCreatePrompt}
          isLoading={isCreating}
          clearInput={clearInput}
        />
      )}
    </div>
  );
};
